import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@services/api';
import type { PurchaseOrder } from '../types/index';
import { DataTable } from '@components/DataTable';
import { StatusBadge } from '@components/StatusBadge';
import { Search, Download, RefreshCw, Link as LinkIcon } from 'lucide-react';
import { format } from 'date-fns';

export const PurchaseOrders: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);

  // Fetch purchase orders from live database
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase-orders', page, pageSize],
    queryFn: async () => {
      try {
        const response = await apiClient.getPurchaseOrders(page, pageSize);
        return response;
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message || 'Failed to fetch purchase orders'
        );
      }
    },
    staleTime: 30000,
    retry: 3,
  });

  // Filter POs based on search and status
  const filteredPOs = React.useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((po: PurchaseOrder) => {
      const matchesSearch =
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.supplierName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || po.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const handleExport = async () => {
    try {
      const csvData = await apiClient.exportLogs('csv');
      const url = window.URL.createObjectURL(new Blob([csvData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `purchase-orders-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'poNumber' as const,
      label: 'PO Number',
      width: '150px',
    },
    {
      key: 'poDate' as const,
      label: 'Date',
      width: '120px',
      render: (value: string) => format(new Date(value), 'MMM dd, yyyy'),
    },
    {
      key: 'supplierName' as const,
      label: 'Supplier',
      width: '200px',
    },
    {
      key: 'amount' as const,
      label: 'Amount',
      width: '120px',
      render: (value: number, row: PurchaseOrder) =>
        `${row.currency} ${value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: 'status' as const,
      label: 'Status',
      width: '120px',
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
            value
          )}`}
        >
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600 mt-1">
            View and manage purchase orders for invoice matching
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by PO or supplier..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="partial">Partial</option>
              <option value="complete">Complete</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className="text-sm text-gray-600">
            {isLoading ? (
              'Loading...'
            ) : error ? (
              <span className="text-red-600">Error: {error.message}</span>
            ) : (
              <>
                Showing {filteredPOs.length} of{' '}
                {data?.pagination?.total || 0} purchase orders
              </>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredPOs}
            loading={isLoading}
            error={error?.message}
            rowKey="id"
            onRowClick={(po) => setSelectedPO(po)}
            pagination={{
              page,
              pageSize,
              total: data?.pagination?.total || 0,
              onPageChange: setPage,
            }}
          />
        </div>

        {/* PO Detail Modal */}
        {selectedPO && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Purchase Order Details
                </h2>
                <button
                  onClick={() => setSelectedPO(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">PO Number</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedPO.poNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
                          selectedPO.status
                        )}`}
                      >
                        {selectedPO.status.charAt(0).toUpperCase() +
                          selectedPO.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">PO Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {format(new Date(selectedPO.poDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Supplier</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedPO.supplierName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedPO.currency}{' '}
                      {selectedPO.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>

                {/* Line Items */}
                {selectedPO.lineItems && selectedPO.lineItems.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      Line Items
                    </p>
                    <div className="space-y-2">
                      {selectedPO.lineItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="text-sm bg-gray-50 p-2 rounded"
                        >
                          <p className="font-medium text-gray-900">
                            {item.description}
                          </p>
                          <p className="text-gray-600">
                            Qty: {item.quantity} × {selectedPO.currency}{' '}
                            {item.unitPrice.toFixed(2)} = {selectedPO.currency}{' '}
                            {item.lineAmount.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="border-t border-gray-200 pt-4 flex gap-2">
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition">
                    <LinkIcon className="w-4 h-4 inline mr-2" />
                    View Matched Invoices
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
